import { Button } from '../../components/ui.tsx';
import { BRAND, BrandIcon, FileTypeIcon } from '../../components/icons.tsx';
import type { ConflictPolicy, NameCheck } from '../../lib/queries.ts';

/**
 * What to do about a name the folder already holds.
 *
 * Uploading the same workbook twice used to end one of two ways, neither of
 * them an answer: a flat refusal, or — because a spreadsheet upload becomes a
 * sheet, and that second step failed quietly on the duplicate name — the file
 * left sitting in the Drive under its raw ".xlsx" filename while the sheet it
 * should have become never appeared.
 *
 * So it is a question now, with the three answers a file explorer gives, in
 * the order they are safe: keep both, replace, or skip. Replace is not styled
 * as the danger it would be elsewhere — what it replaces goes to the trash,
 * not away — but it is second, because "keep both" is the one you can't
 * regret.
 */

/** What the user chose. `null` means skip this file. */
export type ConflictChoice = Exclude<ConflictPolicy, 'error'> | null;

export function UploadConflict({
  filename,
  check,
  remaining,
  action = 'upload',
  onChoose,
  onClose,
}: {
  filename: string;
  check: NameCheck;
  /** How many more files are waiting, so "do this for the rest" is offered. */
  remaining: number;
  /**
   * What the user was in the middle of, which is all that changes in the
   * wording.
   *
   * Three things reach this dialog and they are not the same act: uploading a
   * file, opening a Word file as a document, and creating a new one from the
   * New menu. Telling someone making a blank letter that "opening it would
   * land on top of" theirs describes something they are not doing.
   */
  action?: 'upload' | 'open' | 'create';
  onChoose(choice: ConflictChoice, applyToRest: boolean): void;
  onClose(): void;
}) {
  const decide = (choice: ConflictChoice, applyToRest: boolean) => {
    onChoose(choice, applyToRest);
    onClose();
  };

  const isSheet = check.kind === 'sheet';
  const isDocument = check.kind === 'document';

  return (
    <div className="w-[30rem] max-w-full">
      <div className="flex items-start gap-3 border-b border-border px-5 pb-4 pt-5">
        <span className="mt-0.5 shrink-0" aria-hidden>
          {isSheet && !isDocument ? (
            <BrandIcon src={BRAND.folder} size={24} />
          ) : (
            <FileTypeIcon name={filename} mimeType="" size={24} />
          )}
        </span>
        <div className="min-w-0">
          <h2 className="text-h3 text-ink">This folder already has that name</h2>
          <p className="mt-1 text-body-sm text-body">
            {isSheet || isDocument ? (
              <>
                {isDocument ? 'A document called ' : 'A sheet called '}
                <strong className="font-medium text-ink">
                  {check.suggestion.replace(/\s\(\d+\)$/, '')}
                </strong>{' '}
                is already here.{' '}
                {action === 'create' ? (
                  'Making another under that name would land on top of it.'
                ) : (
                  <>
                    Opening <strong className="font-medium text-ink">{filename}</strong> would land
                    on top of it.
                  </>
                )}
              </>
            ) : (
              <>
                A file called <strong className="font-medium text-ink">{filename}</strong> is
                already here.
              </>
            )}
          </p>
        </div>
      </div>

      <div className="space-y-2 px-5 py-4">
        <Choice
          title="Keep both"
          detail={
            <>
              The new one is saved as{' '}
              <strong className="font-medium text-ink">{check.suggestion}</strong>. Nothing already
              here changes.
            </>
          }
          onSelect={() => decide('rename', false)}
        />
        <Choice
          title="Replace"
          detail="The one already here moves to the trash, where it can be restored, and the new one takes its name."
          onSelect={() => decide('replace', false)}
        />
        <Choice
          title={action === 'upload' ? 'Skip this file' : 'Leave it'}
          detail={
            action === 'create'
              ? 'Nothing is created and nothing here changes.'
              : action === 'open'
                ? 'Nothing is opened and nothing here changes.'
                : 'Nothing is uploaded and nothing here changes.'
          }
          onSelect={() => decide(null, false)}
        />
      </div>

      {remaining > 0 ? (
        <div className="border-t border-border px-5 py-3">
          <p className="mb-2 text-micro text-muted">
            {remaining === 1 ? 'One more file' : `${remaining} more files`} still to upload — apply
            one answer to all of them:
          </p>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => decide('rename', true)}>Keep both, for all</Button>
            <Button onClick={() => decide('replace', true)}>Replace, for all</Button>
            <Button onClick={() => decide(null, true)}>Skip all</Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Choice({
  title,
  detail,
  onSelect,
}: {
  title: string;
  detail: React.ReactNode;
  onSelect(): void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="block w-full rounded-lg border border-border-strong px-4 py-3 text-left transition-fast hover:border-blue-600 hover:bg-blue-50 focus-visible:border-blue-600"
    >
      <span className="block text-body-sm font-medium text-ink">{title}</span>
      <span className="mt-0.5 block text-micro leading-snug text-muted">{detail}</span>
    </button>
  );
}
